/**
 * The commentary currently on screen, and the report input for it.
 *
 * The panel's toolbar (print / report) lives ABOVE the scrolling message
 * list so it stays put while the reader scrolls — a sticky element inside
 * a bubble can only stick within that bubble's own box, which is a few
 * hundred pixels of study card (Brian, UAT 2026-09-21).
 *
 * A finished commentary bubble already carries everything the report
 * needs — the complete handler parks reference, scripture, pericope,
 * cross-refs, variants and the prompt on it — so this reads one message
 * rather than pairing it back up with its source.
 */

import type { ChatMessage } from '@state/chat';
import type { ReportInput } from './rich-message/build-report-markdown';

export interface CommentaryView {
  commentary: string;
  reference: string;
  pericopeTitle?: string;
  originalQuery?: string;
  reportInput: ReportInput;
}

export function commentaryView(messages: ChatMessage[]): CommentaryView | null {
  // Last one wins: a retry replaces its predecessor, and a fresh study
  // clears the panel, so there is only ever one in practice.
  const msg = [...messages]
    .reverse()
    .find((m) => m.commentaryForMessageId && m.commentary && !m.isLoading);
  if (!msg?.commentary || !msg.reference) return null;

  const crossRefs = (msg.crossReferences ?? []).map((ref) => ({
    ref,
    text: msg.crossRefTexts?.[ref],
  }));

  return {
    commentary: msg.commentary,
    reference: msg.reference,
    pericopeTitle: msg.pericope?.title,
    originalQuery: msg.originalQuery,
    reportInput: {
      reference: msg.reference,
      verseText: msg.itIsWritten ?? '',
      pericope: msg.pericope
        ? {
            title: msg.pericope.title,
            book: msg.pericope.book,
            chapter: msg.pericope.chapter,
            startVerse: msg.pericope.startVerse,
            endVerse: msg.pericope.endVerse,
            summary: msg.pericope.summary,
          }
        : null,
      crossRefs,
      variantSummary: msg.variantSummary ?? null,
      // Raw body preferred: training analysis scores the model, not the
      // post-process pipeline.
      aiResponse: msg.rawModelBody ?? msg.commentary,
      prompt: msg.prompt,
      meta: msg.reportMeta,
      readerThoughts: msg.readerThoughts,
    },
  };
}
